/**
 * Shared Dentally API functions.
 * Shared Dentally API functions for availability search, patient management, and appointments.
 */
import { callApi } from './apiCall';
import {
  DENTALLY_MAX_ITEMS,
  DentallyPaginationError,
  parseDentallyPageMeta,
} from './dentallyPagination';
import { hasFeatureAccess } from './featurePermission';

const callerOwnedReadOptions = { suppressServerErrorDispatch: true };

const requireDentallyRows = (rows) => {
  if (!Array.isArray(rows)) {
    throw new DentallyPaginationError('malformed_response');
  }
  return rows;
};

const appendDentallyRows = (allRows, nextRows) => {
  if (allRows.length + nextRows.length > DENTALLY_MAX_ITEMS) {
    throw new DentallyPaginationError('item_limit_exceeded');
  }
  return allRows.concat(nextRows);
};

// Fetch all Dentally practitioners (paginated, returns full array)
export async function fetchDentallyPractitioners(practiceId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  let all = [];
  let page = 1;
  const perPage = 100;
  while (page !== null) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', perPage);
    params.append('sort_by', 'last_login');
    params.append('sort_direction', 'desc');
    // eslint-disable-next-line no-await-in-loop
    const res = await callApi(
      `/api/v1/practices/${practiceId}/dentally/practitioners/?${params.toString()}`,
      'GET',
      null,
      false,
      callerOwnedReadOptions,
    );
    if (!res.ok) throw new Error('Failed to fetch Dentally practitioners');
    const list = requireDentallyRows(res?.data?.practitioners ?? res?.data?.results);
    const pagination = parseDentallyPageMeta(
      res?.data?.meta,
      { requestedPage: page, requestedPerPage: perPage, rowCount: list.length },
    );
    all = appendDentallyRows(all, list);
    page = pagination.nextPage;
  }
  return all;
}

// Fetch all Dentally treatments (paginated, returns full array — Dentally caps per_page at ~100)
export async function fetchDentallyTreatments(practiceId, { activeOnly = true } = {}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  let all = [];
  let page = 1;
  const perPage = 100;
  let hasMore = true;
  while (hasMore) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', perPage);
    if (activeOnly) params.append('active', 'true');
    // eslint-disable-next-line no-await-in-loop
    const res = await callApi(`/api/v1/practices/${practiceId}/dentally/treatments/?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch Dentally treatments');
    const list = res?.data?.treatments || [];
    if (!Array.isArray(list) || list.length === 0) {
      hasMore = false;
    } else {
      all = all.concat(list);
      hasMore = !!res.data?.meta?.next_page;
      page += 1;
    }
  }
  return all;
}

// Fetch calendar diary entries for a month (paginated, returns full array)
export async function fetchDentallyCalendarDiaries(practiceId, monthDate, options = {}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  const toDateStr = (d) => d.toISOString().split('T')[0];
  let all = [];
  const aggregateMeta = {};
  let page = 1;
  while (page !== null) {
    const parts = [
      `after=${toDateStr(startDate)}`,
      `before=${toDateStr(endDate)}`,
    ];
    if (options.practitionerId) parts.push(`practitioner_id=${options.practitionerId}`);
    if (options.siteId) parts.push(`site_id=${options.siteId}`);
    if (options.includeInactive) parts.push('include_inactive_practitioners=true');
    parts.push(`page=${page}`, 'per_page=100');
    // eslint-disable-next-line no-await-in-loop
    const res = await callApi(
      `/api/v1/practices/${practiceId}/dentally/practitioner-diaries/?${parts.join('&')}`,
      'GET',
      null,
      false,
      callerOwnedReadOptions,
    );
    if (!res?.ok) throw new Error('Failed to fetch Dentally calendar diaries');
    const entries = requireDentallyRows(
      res?.data?.practitioner_diaries ?? res?.data?.rota_practitioner_diaries,
    );
    const pagination = parseDentallyPageMeta(
      res?.data?.meta,
      { requestedPage: page, requestedPerPage: 100, rowCount: entries.length },
    );
    const pageMeta = res?.data?.meta;
    if (pageMeta?.partial || pageMeta?.truncated || pageMeta?.complete === false) {
      aggregateMeta.partial = true;
    }
    if (Object.prototype.hasOwnProperty.call(pageMeta || {}, 'skipped_practitioner_diaries_count')) {
      const skippedCount = pageMeta.skipped_practitioner_diaries_count;
      if (!Number.isSafeInteger(skippedCount) || skippedCount < 0) {
        throw new DentallyPaginationError('malformed_response');
      }
      aggregateMeta.skipped_practitioner_diaries_count = (
        aggregateMeta.skipped_practitioner_diaries_count || 0
      ) + skippedCount;
      if (skippedCount > 0) aggregateMeta.partial = true;
    }
    if (Object.prototype.hasOwnProperty.call(pageMeta || {}, 'skipped_practitioner_diaries')) {
      if (!Array.isArray(pageMeta.skipped_practitioner_diaries)) {
        throw new DentallyPaginationError('malformed_response');
      }
      aggregateMeta.skipped_practitioner_diaries = [
        ...(aggregateMeta.skipped_practitioner_diaries || []),
        ...pageMeta.skipped_practitioner_diaries,
      ];
      if (pageMeta.skipped_practitioner_diaries.length > 0) aggregateMeta.partial = true;
    }
    all = appendDentallyRows(all, entries);
    page = pagination.nextPage;
  }
  return { rows: all, meta: aggregateMeta };
}

// Create a Dentally patient (lead sync) — legacy, kept for reference
export async function createDentallyPatient(practiceId, patientData) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally-patients/`,
    'post',
    { patient: patientData },
    false,
    { timeout: 30000 }
  );
}

// Export a CRM lead to Dentally (new endpoint — backend maps all data)
export async function exportLeadToDentally(leadId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/leads-flat/${leadId}/export_to_dentally/`,
    'post',
    {},
    false,
    { timeout: 30000 }
  );
}

export async function linkExistingDentallyPatient(practiceId, leadId, dentallyPatientId) {
  const endpoint = `/api/v1/practices/${practiceId}/dentally-patients/link-existing/`;
  try {
    const response = await callApi(
      endpoint,
      'post',
      { lead_id: leadId, dentally_patient_id: dentallyPatientId },
      false,
      { timeout: 30000 }
    );
    const status = response?.status ?? null;
    const dentallyPatient = response?.data?.dentally_patient;

    if ((status === 200 || status === 201) && dentallyPatient) {
      return {
        ok: true,
        status,
        alreadyLinked: status === 200,
        dentallyPatient,
      };
    }

    return {
      ok: false,
      status,
      code: response?.data?.code || null,
      message: response?.data?.detail || response?.data?.message || 'Unable to link this Dentally patient.',
      retryable: typeof response?.data?.retryable === 'boolean'
        ? response.data.retryable
        : status === 429 || status === 503,
    };
  } catch (error) {
    const status = error?.status ?? error?.response?.status ?? null;
    const errorData = error?.data || error?.response?.data;
    return {
      ok: false,
      status,
      code: errorData?.code || null,
      message: errorData?.detail || error?.message || 'Unable to link this Dentally patient.',
      retryable: typeof errorData?.retryable === 'boolean'
        ? errorData.retryable
        : status === 429 || status === 503,
    };
  }
}

const safeDentallyPatientSummary = (row) => ({
  id: row.id,
  lead: row.lead,
  dentally_patient_id: row.dentally_patient_id,
  source: row.source,
  created_at: row.created_at,
});

export async function fetchDentallyPatientLink(practiceId, leadId) {
  try {
    const response = await callApi(
      `/api/v1/practices/${practiceId}/dentally-patients/?lead=${leadId}&limit=2`
    );
    if (response?.status !== 200) return { outcome: 'error', link: null };

    const rows = response?.data?.results;
    if (!Array.isArray(rows)) return { outcome: 'malformed', link: null };
    if (rows.length === 0) return { outcome: 'none', link: null };

    const allRowsMatchScope = rows.every((row) => (
      row
      && typeof row === 'object'
      && !Array.isArray(row)
      && String(row.lead) === String(leadId)
      && (row.practice == null || String(row.practice) === String(practiceId))
      && (row.practice_id == null || String(row.practice_id) === String(practiceId))
    ));
    if (!allRowsMatchScope) return { outcome: 'malformed', link: null };
    if (rows.length !== 1) return { outcome: 'ambiguous', link: null };

    const patientId = rows[0].dentally_patient_id;
    if (typeof patientId !== 'number' || !Number.isInteger(patientId) || patientId <= 0) {
      return { outcome: 'malformed', link: null };
    }
    return { outcome: 'unique', link: safeDentallyPatientSummary(rows[0]) };
  } catch {
    return { outcome: 'error', link: null };
  }
}

// Fetch available appointment slots from Dentally
export async function fetchDentallyAvailability(practiceId, {
  startTime, finishTime, practitionerIds = [], duration, perPage = 20, page = 1
}, requestOptions = {}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  const params = new URLSearchParams();
  params.append('start_time', startTime);
  params.append('finish_time', finishTime);
  practitionerIds.forEach(id => params.append('practitioner_ids', String(id)));
  if (duration) params.append('duration', String(duration));
  params.append('per_page', String(perPage));
  params.append('page', String(page));
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/appointments/availability/?${params.toString()}`,
    'GET',
    null,
    false,
    {
      ...requestOptions,
      maxRetries: 0,
      bustCache: true,
      suppressServerErrorDispatch: true,
    }
  );
}

// Search Dentally patients by name, email, phone, etc.
export async function searchDentallyPatients(practiceId, query) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  // Backend enforces min_length=2 on `query` — skip the request below that so a
  // single keystroke (or whitespace-only input) never reaches search_patients.
  const trimmed = (query || '').trim();
  if (trimmed.length < 2) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/search_patients/?query=${encodeURIComponent(trimmed)}`
  );
}

// Zones for the slot picker — what Dentally's UI calls Zones and its API calls
// `session_appointments`.
//
// Advisory only. Dentally's availability is the rota minus booked appointments
// and nothing else, so a free period says nothing about what the practice
// reserved that time FOR. This fills that gap; it never removes slots.
//
// The backend returns 200 with an empty list on a provider failure, so a
// caller can render availability unchanged when zones are unavailable.
export async function fetchDentallyZones(practiceId, { after, before, practitionerId } = {}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  if (!practiceId || !after || !before) return null;
  const params = new URLSearchParams({ after, before });
  if (practitionerId != null) params.set('practitioner_id', String(practitionerId));
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/appointments/zones/?${params.toString()}`
  );
}

// Dentally appointment creation. Patient preparation/export is coordinated by
// the durable backend action; one UI action must produce one create POST.
export async function createDentallyAppointmentAsync(practiceId, appointmentPayload) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  if (!appointmentPayload?.lead_id || !appointmentPayload?.crm_appointment_id) {
    return {
      ok: false,
      status: 409,
      retryable: false,
      data: {
        code: 'durable_appointment_required',
        detail: 'A saved CRM appointment is required before booking in Dentally.',
        retryable: false,
      },
    };
  }
  const payload = { ...appointmentPayload, state: 'Pending' };
  const url = `/api/v1/practices/${practiceId}/dentally/appointments/create/`;

  return callApi(url, 'post', payload, false, { timeout: 30000 });
}

export async function cancelAppointmentWithDentally(practiceId, leadId, crmAppointmentId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  if (!practiceId || !leadId || !crmAppointmentId) {
    return {
      ok: false,
      status: 409,
      retryable: false,
      data: {
        code: 'durable_appointment_required',
        detail: 'A saved CRM appointment is required before cancelling in Dentally.',
        retryable: false,
      },
    };
  }

  const response = await callApi(
    `/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${crmAppointmentId}/cancel-with-dentally/`,
    'post',
  );
  const migrationRequired = [400, 409].includes(response?.status)
    && response?.data?.code === 'durable_cancellation_required';

  return migrationRequired
    ? { ...response, retryable: false, migrationRequired: true }
    : response;
}

/**
 * Propagate a CRM appointment edit to Dentally, durably.
 *
 * This is the ONLY write path to Dentally for an appointment edit — the generic
 * PUT saves the CRM and makes no provider call — so it must be sent for every
 * linked appointment, not only when something intends to show its result.
 *
 * The CRM write is authoritative and has already happened; this reports what
 * the PROVIDER did. The backend is idempotent against the link's
 * `synced_datetime`, so a re-delivery (retry, double submit, saving the same
 * time twice) answers `already_synced` and writes nothing.
 *
 * Non-2xx is an expected answer here, not a transport failure: a 409 / 422 /
 * 503 carries the real outcome in its body and MUST reach the caller rather
 * than becoming a generic error toast — the CRM edit saved either way.
 */
export async function rescheduleAppointmentWithDentally(practiceId, leadId, crmAppointmentId, {
  datetime,
  note,
  expectedDentallyAppointmentId,
} = {}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  if (!practiceId || !leadId || !crmAppointmentId) {
    return {
      ok: false,
      status: 409,
      data: {
        outcome: 'rejected',
        code: 'durable_appointment_required',
        retryable: false,
      },
    };
  }
  if (datetime == null && note == null) {
    return {
      ok: false,
      status: 400,
      data: {
        outcome: 'rejected',
        code: 'nothing_to_propagate',
        retryable: false,
      },
    };
  }

  const body = {};
  if (datetime != null) body.datetime = datetime;
  if (note != null) body.note = note;
  if (expectedDentallyAppointmentId != null) {
    body.expected_dentally_appointment_id = Number(expectedDentallyAppointmentId);
  }

  return callApi(
    `/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${crmAppointmentId}/reschedule-with-dentally/`,
    'post',
    body,
    false,
    { timeout: 30000, maxRetries: 0, suppressServerErrorDispatch: true },
  );
}

// Update (PATCH) an existing Dentally appointment
export async function updateDentallyAppointment(practiceId, appointmentId, appointmentData) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/appointments/${appointmentId}/update/`,
    'patch',
    appointmentData,
    false,
    { timeout: 30000 }
  );
}

// Fetch the primary fee (price) for a Dentally treatment.
// Returns the numeric price_one value, or 0 if no fee exists.
export async function fetchDentallyTreatmentFee(practiceId, dentallyTreatmentId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  if (!practiceId || !dentallyTreatmentId) return 0;
  const res = await callApi(
    `/api/v1/practices/${practiceId}/dentally/fees/?treatment_id=${dentallyTreatmentId}`
  );
  if (!res.ok) return 0;
  const fees = res.data?.fees || [];
  if (fees.length === 0) return 0;
  const price = parseFloat(fees[0].price_one);
  return Number.isFinite(price) && price > 0 ? price : 0;
}

// Fetch every active appointment reason; incomplete lists must not validate bookings.
export async function fetchDentallyAppointmentReasons(practiceId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  let all = [];
  let page = 1;
  const perPage = 100;
  while (page !== null) {
    // eslint-disable-next-line no-await-in-loop
    const res = await callApi(
      `/api/v1/practices/${practiceId}/dentally/appointment-reasons/?deleted=false&page=${page}&per_page=${perPage}`,
      'GET', null, false, callerOwnedReadOptions,
    );
    if (!res.ok) throw new Error('Failed to fetch appointment reasons');
    const rows = requireDentallyRows(res.data?.appointment_reasons ?? res.data?.results);
    const pagination = parseDentallyPageMeta(res.data?.meta, {
      requestedPage: page, requestedPerPage: perPage, rowCount: rows.length,
    });
    all = appendDentallyRows(all, rows);
    page = pagination.nextPage;
  }
  return all.filter((reason) => !reason.deleted);
}
